import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useHistory } from "react-router-dom";
import { downloadReportArtifact } from "src/views/assistant/records/downloadReportArtifact";
import { normalizeMultiline } from "src/views/assistant/records/normalizeMultiline";
import { AgentMarkdown } from "src/views/assistant/records/AgentMarkdown";
import type { FfnRecordRow } from "../services/FfnService";

export const VERIFICATION_TIER_COLOR: Record<
  string,
  "default" | "success" | "warning" | "error"
> = {
  corroborated: "success",
  verified_with_gaps: "warning",
  unsupported_claims: "warning",
  contradicted: "error",
};

const CLAIM_VERDICT_COLOR: Record<
  string,
  "default" | "success" | "warning" | "error"
> = {
  supported: "success",
  partially_supported: "warning",
  unsupported: "warning",
  contradicted: "error",
  not_independently_verifiable: "default",
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="overline"
    sx={{ display: "block", color: "text.secondary", lineHeight: 2, mt: 2 }}
  >
    {children}
  </Typography>
);

const JustificationBlock = ({
  label,
  answer,
  justification,
  citations,
}: {
  label: string;
  answer?: boolean;
  justification?: string;
  citations?: string[];
}) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {answer !== undefined && (
        <Chip
          label={answer ? "YES" : "NO"}
          size="small"
          color={answer ? "warning" : "default"}
          sx={{ fontWeight: 600 }}
        />
      )}
    </Box>
    {justification && (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.25, whiteSpace: "pre-wrap" }}
      >
        {normalizeMultiline(justification)}
      </Typography>
    )}
    {citations && citations.length > 0 && (
      <Typography variant="caption" color="text.secondary">
        Citations: {citations.join("; ")}
      </Typography>
    )}
  </Box>
);

interface FfnRecordDetailDialogProps {
  record: FfnRecordRow | null;
  onClose: () => void;
  onResume: (recordId: number) => void;
  resumingRecordId: number | null;
  onVerify: (recordId: number) => void;
  verifyingRecordId: number | null;
}

/**
 * The full 50.59 record view: recorded answers with justifications and
 * citations, independent-challenge grades, claim-by-claim report
 * verification, conclusion and research log. Extracted verbatim from the
 * original FfnRecordsPage dialog; record conversations now open in the
 * dedicated 50.59 chat, while verification conversations stay in Atom
 * Assist (they are verification dialogues, not 50.59 ones).
 */
const FfnRecordDetailDialog = ({
  record,
  onClose,
  onResume,
  resumingRecordId,
  onVerify,
  verifyingRecordId,
}: FfnRecordDetailDialogProps) => {
  const history = useHistory();

  const screeningQuestions = useMemo(
    () => [
      {
        key: "is_change_to_facility_as_described",
        just: "q1_justification",
        label: "Q1 — Change to facility as described in the UFSAR",
      },
      {
        key: "is_change_to_procedures_as_described",
        just: "q2_justification",
        label: "Q2 — Change to procedures as described in the UFSAR",
      },
      {
        key: "is_adverse_change_to_methods_of_evaluation",
        just: "q3_justification",
        label: "Q3 — Adverse change to methods of evaluation",
      },
      {
        key: "is_test_or_experiment_not_described",
        just: "q4_justification",
        label: "Q4 — Test or experiment not described in the UFSAR",
      },
    ],
    [],
  );

  return (
    <Dialog open={record !== null} onClose={onClose} maxWidth="md" fullWidth>
      {record && (
        <>
          <DialogTitle sx={{ pr: 6 }}>
            <Typography variant="h6" component="div">
              Record #{record.id} — {record.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {record.plant_name ?? `Plant ${record.plant_id}`} ·{" "}
              {record.status.replace(/_/g, " ")}
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              {record.dialogue_id && (
                <Button
                  size="small"
                  startIcon={<ForumOutlinedIcon />}
                  onClick={() =>
                    history.push(`/fifty-fifty-nine/chat/${record.dialogue_id}`)
                  }
                >
                  Open conversation
                </Button>
              )}
              {record.report_artifact_id ? (
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() =>
                    downloadReportArtifact(
                      record.report_artifact_id as number,
                      `50_59_record_${record.id}_report.docx`,
                    )
                  }
                >
                  Download report
                </Button>
              ) : (
                <Button
                  size="small"
                  startIcon={<PlayArrowIcon />}
                  disabled={resumingRecordId !== null}
                  onClick={() => onResume(record.id)}
                >
                  Resume with agent
                </Button>
              )}
              {record.report_artifact_id &&
                record.report_verification?.status !== "pending" && (
                  <Button
                    size="small"
                    startIcon={
                      verifyingRecordId === record.id ? (
                        <CircularProgress size={12} color="inherit" />
                      ) : (
                        <FactCheckOutlinedIcon fontSize="small" />
                      )
                    }
                    disabled={verifyingRecordId !== null}
                    onClick={() => onVerify(record.id)}
                    data-testid="ffn-record-run-verification"
                  >
                    {record.report_verification
                      ? "Re-run verification"
                      : "Run verification"}
                  </Button>
                )}
            </Box>

            <SectionTitle>Activity</SectionTitle>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {normalizeMultiline(record.activity_description)}
            </Typography>

            <SectionTitle>Applicability (NEI 96-07 §4.1)</SectionTitle>
            {record.applicability ? (
              <JustificationBlock
                label={
                  record.applicability.fifty_fifty_nine_applies
                    ? "10 CFR 50.59 applies"
                    : "10 CFR 50.59 does not apply"
                }
                justification={record.applicability.justification}
                citations={record.applicability.citations}
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Not recorded
              </Typography>
            )}

            <SectionTitle>Screening (§4.2)</SectionTitle>
            {record.screening ? (
              <>
                {screeningQuestions.map((q) => (
                  <JustificationBlock
                    key={q.key}
                    label={q.label}
                    answer={record.screening?.[q.key]}
                    justification={record.screening?.[q.just]}
                  />
                ))}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {record.screening.screens_in
                    ? "SCREENS IN — evaluation required"
                    : "SCREENS OUT"}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Not recorded
              </Typography>
            )}

            {record.evaluation && (
              <>
                <SectionTitle>
                  Evaluation — 10 CFR 50.59(c)(2) criteria
                </SectionTitle>
                {Object.keys(record.evaluation)
                  .sort((a, b) => Number(a) - Number(b))
                  .map((n) => {
                    const c = record.evaluation?.[n];
                    return (
                      <JustificationBlock
                        key={n}
                        label={`Criterion ${n}`}
                        answer={c?.criterion_met}
                        justification={c?.justification}
                        citations={c?.citations}
                      />
                    );
                  })}
              </>
            )}

            <SectionTitle>Independent Challenge</SectionTitle>
            {record.challenge?.grades ? (
              <>
                <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
                  <Chip
                    label={record.challenge.passed ? "PASSED" : "FAILED"}
                    color={record.challenge.passed ? "success" : "error"}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Table size="small">
                  <TableBody>
                    {record.challenge.grades.map((g) => (
                      <TableRow key={g.item_id}>
                        <TableCell sx={{ width: 56, fontWeight: 600 }}>
                          {g.item_id}
                        </TableCell>
                        <TableCell sx={{ width: 70 }}>
                          <Chip
                            label={g.verdict.replace(/_/g, " ")}
                            size="small"
                            color={
                              g.verdict === "pass"
                                ? "success"
                                : g.verdict === "fail"
                                  ? "error"
                                  : g.verdict === "weak"
                                    ? "warning"
                                    : "default"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {normalizeMultiline(g.evidence)}
                            {g.required_revision
                              ? ` — Required revision: ${normalizeMultiline(g.required_revision)}`
                              : ""}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {record.challenge.summary && (
                  <AgentMarkdown
                    text={record.challenge.summary}
                    sx={{ fontSize: 13, mt: 1 }}
                  />
                )}
                {record.challenge.narrative && (
                  <Box
                    component="details"
                    sx={{
                      mt: 1.5,
                      "& summary": {
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "text.secondary",
                      },
                    }}
                  >
                    <summary>Independent review (full write-up)</summary>
                    <AgentMarkdown
                      text={record.challenge.narrative}
                      sx={{
                        fontSize: 14,
                        mt: 1,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No challenge recorded
              </Typography>
            )}

            <SectionTitle>Independent Report Verification</SectionTitle>
            {!record.report_artifact_id ? (
              <Typography variant="caption" color="text.secondary">
                Available once the record is finalized — the verification
                reviews the final report document itself.
              </Typography>
            ) : (
              <>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  {record.report_verification?.status === "complete" &&
                    record.report_verification.tier && (
                      <Chip
                        label={record.report_verification.tier.replace(
                          /_/g,
                          " ",
                        )}
                        size="small"
                        color={
                          VERIFICATION_TIER_COLOR[
                            record.report_verification.tier
                          ] ?? "default"
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  {record.report_verification?.status === "pending" && (
                    <Chip
                      label="Verification in progress…"
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {record.report_verification?.status === "failed" && (
                    <Chip
                      label="Verification failed — re-run"
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  )}
                  {record.report_verification?.dialogue_id && (
                    <Button
                      size="small"
                      startIcon={<ForumOutlinedIcon />}
                      onClick={() =>
                        history.push(
                          `/assistant/chat/${record.report_verification?.dialogue_id}`,
                        )
                      }
                    >
                      Open verification conversation
                    </Button>
                  )}
                </Box>
                {record.report_verification?.status === "complete" &&
                  record.report_verification.claims && (
                    <>
                      <Table size="small">
                        <TableBody>
                          {record.report_verification.claims.map(
                            (claim, idx) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ width: 110 }}>
                                  <Chip
                                    label={claim.verdict.replace(/_/g, " ")}
                                    size="small"
                                    color={
                                      CLAIM_VERDICT_COLOR[claim.verdict] ??
                                      "default"
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", fontWeight: 600 }}
                                  >
                                    {claim.claim}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block" }}
                                  >
                                    {claim.document_location}
                                    {claim.cited_source
                                      ? ` · cites ${claim.cited_source}`
                                      : ""}{" "}
                                    — {claim.evidence}
                                  </Typography>
                                  {claim.research_direction && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        color: "warning.main",
                                      }}
                                    >
                                      Check: {claim.research_direction}
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                      {record.report_verification.summary && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 1 }}
                        >
                          {record.report_verification.summary}
                        </Typography>
                      )}
                    </>
                  )}
                {!record.report_verification && (
                  <Typography variant="caption" color="text.secondary">
                    Not yet verified. The verification independently re-checks
                    every claim in the final report against the plant&apos;s
                    licensing basis and flags exactly what a reviewer should
                    confirm by hand.
                  </Typography>
                )}
              </>
            )}

            {record.conclusion && (
              <>
                <SectionTitle>Conclusion</SectionTitle>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {normalizeMultiline(record.conclusion)}
                </Typography>
              </>
            )}

            {record.research_log.length > 0 && (
              <>
                <SectionTitle>
                  Research Log ({record.research_log.length})
                </SectionTitle>
                {record.research_log.map((entry, idx) => (
                  <Box key={idx} sx={{ mb: 0.75 }}>
                    <Typography
                      variant="caption"
                      sx={{ whiteSpace: "pre-wrap" }}
                    >
                      <b>[{entry.source}]</b>
                      {entry.reference ? ` ${entry.reference}` : ""} —{" "}
                      {normalizeMultiline(entry.note)}
                    </Typography>
                    <Divider sx={{ mt: 0.75 }} />
                  </Box>
                ))}
              </>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};

export default FfnRecordDetailDialog;
